// src/context/CookieConsentContext.js
// Cookie consent banner and preference management for GDPR/PECR compliance.
// Web-only — renders nothing on native platforms.

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { View, Text, TouchableOpacity, Switch, StyleSheet, Platform, Linking } from 'react-native';
import { isDesktopApp } from '../utils/platform';

const STORAGE_KEY = 'critcash_cookie_consent';
const DESKTOP_ESSENTIAL_CONSENT = Object.freeze({ essential: true, analytics: false, marketing: false });

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => useContext(CookieConsentContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);       // null = not yet decided
  const [bannerVisible, setBannerVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);   // expanded preferences view

  // Drafts for the toggles before the user hits "Save"
  const [draftAnalytics, setDraftAnalytics] = useState(true);
  const [draftMarketing, setDraftMarketing] = useState(false);

  // Hydrate from localStorage on mount (web only). Inside the CritCast desktop
  // app (S8.7), skip the banner and record essential-only consent on every
  // partition (including Shared PC mode after sign-out).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (isDesktopApp()) {
      setConsent(DESKTOP_ESSENTIAL_CONSENT);
      setBannerVisible(false);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(DESKTOP_ESSENTIAL_CONSENT)); } catch {}
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setConsent(JSON.parse(stored));
      } else {
        setBannerVisible(true);
      }
    } catch {
      setBannerVisible(true);
    }
  }, []);

  // Persist consent to localStorage
  const persistConsent = useCallback((value) => {
    setConsent(value);
    setBannerVisible(false);
    setShowPrefs(false);
    if (Platform.OS === 'web') {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch {}
    }
  }, []);

  // Accept all categories
  const acceptAll = useCallback(() => {
    persistConsent({ essential: true, analytics: true, marketing: true });
  }, [persistConsent]);

  // Reject non-essential (essential only)
  const rejectNonEssential = useCallback(() => {
    persistConsent({ essential: true, analytics: false, marketing: false });
  }, [persistConsent]);

  // Save granular preferences
  const savePreferences = useCallback(() => {
    persistConsent({ essential: true, analytics: draftAnalytics, marketing: draftMarketing });
  }, [persistConsent, draftAnalytics, draftMarketing]);

  // Re-open the banner (called from Footer "Cookie Preferences" link)
  const resetConsent = useCallback(() => {
    // Pre-fill toggles with current consent values
    if (consent) {
      setDraftAnalytics(consent.analytics ?? true);
      setDraftMarketing(consent.marketing ?? false);
    }
    setShowPrefs(true);
    setBannerVisible(true);
  }, [consent]);

  const value = { consent, acceptAll, rejectNonEssential, resetConsent };

  // On native platforms, just render children — no banner needed
  if (Platform.OS !== 'web') {
    return (
      <CookieConsentContext.Provider value={value}>
        {children}
      </CookieConsentContext.Provider>
    );
  }

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      {bannerVisible && (
        <View style={styles.overlay}>
          <View style={styles.banner}>
            {/* Header */}
            <Text style={styles.title}>Cookie Preferences</Text>

            <Text style={styles.body}>
              We use cookies and similar technologies to keep our platform running (authentication, security)
              and to understand how it's used. You can choose which optional cookies to allow below.
            </Text>

            {/* Preferences panel */}
            {showPrefs ? (
              <View style={styles.prefsContainer}>
                {/* Essential — always on */}
                <View style={styles.prefRow}>
                  <View style={styles.prefLabel}>
                    <Text style={styles.prefName}>Essential</Text>
                    <Text style={styles.prefDesc}>Authentication, security, app functionality</Text>
                  </View>
                  <Switch
                    value={true}
                    disabled
                    trackColor={{ false: '#333', true: '#4a4116' }}
                    thumbColor="#FCD000"
                  />
                </View>

                {/* Analytics */}
                <View style={styles.prefRow}>
                  <View style={styles.prefLabel}>
                    <Text style={styles.prefName}>Analytics</Text>
                    <Text style={styles.prefDesc}>Helps us understand usage and improve the platform</Text>
                  </View>
                  <Switch
                    value={draftAnalytics}
                    onValueChange={setDraftAnalytics}
                    trackColor={{ false: '#333', true: '#4a4116' }}
                    thumbColor={draftAnalytics ? '#FCD000' : '#666'}
                  />
                </View>

                {/* Marketing / Ad tracking */}
                <View style={styles.prefRow}>
                  <View style={styles.prefLabel}>
                    <Text style={styles.prefName}>Marketing</Text>
                    <Text style={styles.prefDesc}>Ad performance and relevance tracking</Text>
                  </View>
                  <Switch
                    value={draftMarketing}
                    onValueChange={setDraftMarketing}
                    trackColor={{ false: '#333', true: '#4a4116' }}
                    thumbColor={draftMarketing ? '#FCD000' : '#666'}
                  />
                </View>

                {/* Save / Cancel */}
                <View style={styles.prefsActions}>
                  <TouchableOpacity style={styles.btnSave} onPress={savePreferences}>
                    <Text style={styles.btnSaveText}>Save Preferences</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.btnCancel} onPress={() => { setBannerVisible(false); setShowPrefs(false); }}>
                    <Text style={styles.btnCancelText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              /* Quick action buttons */
              <View style={styles.actions}>
                <TouchableOpacity style={styles.btnAccept} onPress={acceptAll}>
                  <Text style={styles.btnAcceptText}>Accept All</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.btnReject} onPress={rejectNonEssential}>
                  <Text style={styles.btnRejectText}>Essential Only</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.btnManage} onPress={() => { setShowPrefs(true); }}>
                  <Text style={styles.btnManageText}>Manage Preferences</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Privacy policy link */}
            <Text
              style={styles.privacyLink}
              onPress={() => Linking.openURL('https://critcash.com/privacy-policy')}
            >
              Privacy Policy
            </Text>
          </View>
        </View>
      )}
    </CookieConsentContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  banner: {
    backgroundColor: '#1a1a1b',
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 12,
    padding: 20,
    maxWidth: 520,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 20,
  },
  title: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  body: {
    color: '#AAA',
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 16,
  },

  // Quick action buttons row
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  btnAccept: {
    backgroundColor: '#FCD000',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  btnAcceptText: {
    color: '#000',
    fontWeight: '700',
    fontSize: 13,
  },
  btnReject: {
    backgroundColor: '#2a2a2b',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
  },
  btnRejectText: {
    color: '#CCC',
    fontWeight: '600',
    fontSize: 13,
  },
  btnManage: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  btnManageText: {
    color: '#FCD000',
    fontWeight: '600',
    fontSize: 13,
    textDecorationLine: 'underline',
  },

  // Preferences panel
  prefsContainer: {
    marginTop: 4,
  },
  prefRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  prefLabel: {
    flex: 1,
    marginRight: 12,
  },
  prefName: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  prefDesc: {
    color: '#888',
    fontSize: 12,
    marginTop: 2,
  },
  prefsActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 14,
  },
  btnSave: {
    backgroundColor: '#FCD000',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  btnSaveText: {
    color: '#000',
    fontWeight: '700',
    fontSize: 13,
  },
  btnCancel: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  btnCancelText: {
    color: '#888',
    fontSize: 13,
  },

  // Privacy link
  privacyLink: {
    color: '#888',
    fontSize: 12,
    textDecorationLine: 'underline',
    marginTop: 12,
    textAlign: 'center',
  },
});
